import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from '../../../store'
import { AccountData } from '../../modules/accountSelection'
import { ProductType } from '../../modules/partner/reducer'
import { CloudType } from '../../modules/types'
import { CloudAccountType } from '@labrav/react-components'
import { parseProductPathSegments } from '../parseProductPathSegments'

const safeHead = <T>(list: T[] | undefined | null): T | undefined => {
  if (!Array.isArray(list) || list.length === 0) return undefined
  return list[0]
}

const EMPTY_ACCOUNTS: AccountData[] = []

export const useAccountId = ({
  product,
  cloud,
}: {
  product: ProductType
  cloud?: CloudType
}) => {
  const { accountId: accountIdFromParams } = useParams<{ accountId: string }>()
  const rawAccountIdFromPath = parseProductPathSegments(
    location.pathname,
    product
  )?.accountId
  // Only trust the path-extracted value if it is a UUID — the regex in
  // parseProductPathSegments is naive and will match non-UUID path segments
  // (e.g. 'cloud-settings' on /oppsync/onboarding/cloud-settings).
  const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  const accountIdFromPath =
    rawAccountIdFromPath && UUID_REGEX.test(rawAccountIdFromPath)
      ? rawAccountIdFromPath
      : undefined

  const selectedAccountIds = useSelector(
    state => state.accountSelection?.selectedAccountIds
  )
  const availableAccounts = useSelector(state => {
    const accounts = state.accountSelection?.availableAccounts
    return Array.isArray(accounts) ? accounts : EMPTY_ACCOUNTS
  })
  const accountData = useMemo(
    () =>
      availableAccounts.filter(
        account => account?.product?.toLowerCase() === product
      ),
    [availableAccounts, product]
  )

  // If accountId is in route params, use it directly
  if (accountIdFromParams) {
    return accountIdFromParams
  }

  if (accountIdFromPath) {
    return accountIdFromPath
  }

  const accountIdForCloud = (requestedCloud: CloudType) =>
    accountData.find(account => account?.cloud === requestedCloud)?.id || ''

  // For FLYOUT, read from selectedAccountIds first (single source of truth)
  if (product.toLowerCase() === 'flyout') {
    // Reducer stores FLYOUT as uppercase, but ProductType is lowercase
    const flyoutSelections =
      (
        selectedAccountIds as Record<string, Partial<Record<CloudType, string>>>
      )?.['FLYOUT'] || selectedAccountIds?.['flyout']

    if (cloud) {
      return flyoutSelections?.[cloud] || accountIdForCloud(cloud)
    }

    const selectedCloud = Object.keys(flyoutSelections || {})[0] as
      | CloudType
      | undefined
    if (selectedCloud) {
      return flyoutSelections?.[selectedCloud] || ''
    }
  }

  if (cloud) {
    return accountIdForCloud(cloud)
  }
  return safeHead(accountData)?.id || ''
}

// Extended CloudAccountType to include originalId for handling duplicate IDs across clouds
export type CloudAccountTypeWithOriginalId = CloudAccountType & {
  originalId: string
}

/**
 * Extracts the original accountId from a combined ID format.
 * Combined format: "accountId-product-cloud" (e.g., "123-flyout-AWS")
 *
 * @param combinedId - The combined ID string
 * @returns The original account ID
 */
export const extractAccountIdFromCombinedId = (combinedId: string): string => {
  // Split by '-' and take the first part (accountId)
  // Handle case where accountId itself might contain '-'
  const parts = combinedId.split('-')
  if (parts.length >= 3) {
    // Last two parts are product and cloud, everything before is accountId
    return parts.slice(0, -2).join('-')
  }
  return combinedId // Fallback to the ID itself if format is not as expected
}

/**
 * Gets the selected account ID with cloud for matching in dropdown.
 * Creates a combined ID in format: "accountId-product-cloud"
 *
 * @param accountId - The account ID from route params
 * @param product - The product type
 * @param cloud - The cloud provider
 * @returns Combined ID or original accountId if cloud not provided
 */
export const getSelectedAccountIdWithCloud = (
  accountId: string,
  product: ProductType,
  cloud?: string
): string => {
  if (!cloud || !accountId) return accountId
  const cloudUpper = cloud.toUpperCase()
  return `${accountId}-${product}-${cloudUpper}`
}

const EMPTY_ACCOUNTS_FOR_CLOUD: AccountData[] = []

/**
 * Hook to get cloud accounts with unique IDs.
 * Creates unique IDs by combining accountId+product+cloud to handle cases where
 * two accounts have the same ID but different clouds.
 *
 * @param product - The product type (e.g., 'flyout', 'oppsync', 'cloudfaas')
 * @returns Array of cloud accounts with unique IDs in format: "accountId-product-cloud"
 */
export const useCloudAccounts = (product: ProductType) => {
  const accountsData = useSelector(state => {
    const accounts = state.accountSelection.availableAccounts
    return Array.isArray(accounts) ? accounts : EMPTY_ACCOUNTS_FOR_CLOUD
  })

  return useMemo(() => {
    const marketplaceAccountsData = accountsData.filter(
      account => account.product?.toLowerCase() === product
    )

    const toProvider = (cloud: unknown): 'AWS' | 'AZURE' | 'GCP' | null => {
      const value = typeof cloud === 'string' ? cloud.toUpperCase() : ''
      if (value === 'AWS' || value === 'AZURE' || value === 'GCP') return value
      return null
    }

    return marketplaceAccountsData
      .map(account => {
        const provider = toProvider(account.cloud)
        if (!provider) return null
        // Create unique ID: "accountId-product-cloud" (e.g., "123-flyout-AWS")
        const uniqueId = `${account.id}-${product}-${provider}`
        return {
          id: uniqueId,
          name: account.name,
          provider,
          originalId: account.id, // Keep original ID for reference
        }
      })
      .filter(Boolean) as CloudAccountTypeWithOriginalId[]
  }, [accountsData, product])
}
